import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Gavel, Loader2, Pencil } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { SwipePager } from '../components/ui/swipe-pager';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '../components/ui/table';
import { useToast } from '../components/ui/use-toast';
import { cn } from '../lib/utils';
import { BoulderActionButtons } from '../components/competition/BoulderActionButtons';
import { BoulderAttemptMarkers } from '../components/competition/BoulderAttemptMarkers';
import { BoulderEditDialog } from '../components/competition/BoulderEditDialog';
import { type LeadCardDraft } from '../components/competition/LeadCardEditor';
import { LeadCardSummary } from '../components/competition/LeadCardSummary';
import { LeadScoringDialog } from '../components/competition/LeadScoringDialog';
import { RefreshFlash } from '../components/competition/RefreshFlash';
import { RoundClock, RoundClockSubject } from '../components/competition/RoundClock';
import { useRoundTimeUp } from '../components/competition/round-clock';
import { competitionCrumb } from '../components/competition/competition-crumb';
import { RoundHelperStrip } from '../components/competition/round-delegation/RoundHelperStrip';
import { useRoundDelegation } from '../components/competition/round-delegation/round-delegation-context';
import {
  RefreshGenerationContext,
  boulderCardSignature,
  leadCardSignature,
  useRefreshGeneration,
} from '../components/competition/refresh-flash';
import {
  permittedLeadStyles,
  type LeadClassStyles,
} from '../components/competition/lead-climb-style';
import { roundRefreshOptions, useAutoRefresh } from '../hooks/useAutoRefresh';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { createSerialQueue } from '../lib/serial-queue';
import { byAthleteName } from '../utils/athlete-order';
import { errorCodeOf } from '../services/api-error';
import { competitionRoundsService } from '../services/competition-rounds';
import { competitionsService } from '../services/competitions';
import {
  roundScoresService,
  type AttemptType,
  type RoundScore,
  type RoundScoreClimb,
  type ScoreCard,
} from '../services/round-scores';
import type { CompetitionRound, ScoredAttempt } from '../types/competition-round';
import type { Competition } from '../types/competition';


/** A refusal that means the round stopped taking a squad's marks under the page. */
const roundShut = (error: unknown) => {
  const code = errorCodeOf(error);
  return code === 'round_closed' || code === 'round_in_verification';
};

/**
 * The athlete last picked in a round, so coming back to it — from the
 * competition page, or after a reload — opens on them rather than on the first
 * name of the list.
 */
const ATHLETE_STORAGE_PREFIX = 'team-athlete:';

function loadStoredAthlete(slug: string | undefined, roundId: string | undefined): string | null {
  if (!slug || !roundId) return null;
  try {
    return sessionStorage.getItem(`${ATHLETE_STORAGE_PREFIX}${slug}:${roundId}`) || null;
  } catch {
    return null;
  }
}

function storeAthlete(slug: string | undefined, roundId: string | undefined, participantId: string) {
  if (!slug || !roundId) return;
  try {
    sessionStorage.setItem(`${ATHLETE_STORAGE_PREFIX}${slug}:${roundId}`, participantId);
  } catch {
    // Private mode or blocked storage: the round opens on its first athlete.
  }
}

/**
 * Whether the pager has already shown, in this round, that the card swipes to
 * the next athlete (SwipePager's `peek`). Once is enough: from the second time
 * it would only be in the way. Kept for good, unlike the athlete above — it is
 * the reader who has learned, not the session.
 */
const SWIPE_HINT_STORAGE_PREFIX = 'team-swipe-hint:';

function swipeHintSeen(slug: string | undefined, roundId: string | undefined): boolean {
  if (!slug || !roundId) return true;
  try {
    return localStorage.getItem(`${SWIPE_HINT_STORAGE_PREFIX}${slug}:${roundId}`) !== null;
  } catch {
    // Storage blocked: better never shown than shown every time.
    return true;
  }
}

function storeSwipeHintSeen(slug: string | undefined, roundId: string | undefined) {
  if (!slug || !roundId) return;
  try {
    localStorage.setItem(`${SWIPE_HINT_STORAGE_PREFIX}${slug}:${roundId}`, '1');
  } catch {
    // Private mode or blocked storage: answered above, it is never shown.
  }
}

/**
 * One athlete's whole round, as their squad's team manager — or a parent, or
 * the desk — works down it.
 *
 * The transpose of the judge's page, and deliberately the same table: a judge
 * stands at one problem and works through the people, while a team manager has
 * their own few athletes and marks each one's card problem by problem. So the
 * rows here are the **blocks** where the judge's are the climbers, and everything
 * else — the marks, the pencil, the three buttons on the open row — is the same
 * furniture, because it is the same job done the other way round.
 *
 * **The athletes are pages.** The board is read once for the round, and each
 * athlete is a page of the same pager the judge's classes use: swiped sideways
 * to the next name, or picked from the list the athlete's name opens in the
 * navbar.
 *
 * **Only this athlete's own blocks.** The card the server sends is already
 * narrowed to the problems their class climbs, plus the ones the whole round
 * shares (see the scoreboard's `climbs_for`), so the rows are the card and
 * nothing is filtered here. Whether a row opens is the server's `can_score`:
 * a judge's block is read-only for a team manager or a parent, and open to the
 * desk (RoundClimb#scorable_by?).
 *
 * Marking sends the card the problem should end up with (the scoreboard's
 * replacement model, see roundScoresService.save) rather than a stream of edits,
 * so a failed tap leaves the card as it was and the button says so.
 *
 * **On a helper's phone too** (RoundHelperGate): the athletes a team manager
 * handed over, under the link the phone came in by. The board is the same
 * call, narrowed by the server to them; whether the round still takes the
 * phone's marks is the round's own `can_write`, and every way out of the page
 * stays under the link, since the helper is nobody the app knows.
 */
export default function TeamManagerAthletePage() {
  const { slug, roundId, participantId } = useParams<{
    slug: string;
    roundId: string;
    participantId: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const helping = useRoundDelegation();

  /** One athlete's page: under the helper's link on a helper's phone. */
  const helperBase = helping?.basePath ?? null;
  const athletePath = useCallback(
    (id: string) =>
      helperBase ? `${helperBase}/athletes/${id}` : `/competitions/${slug}/rounds/${roundId}/athletes/${id}`,
    [helperBase, slug, roundId]
  );

  /** The stage, for its logo at the head of the navbar and nothing else. */
  const [competition, setCompetition] = useState<Competition | null>(null);
  const [round, setRound] = useState<CompetitionRound | null>(null);
  /**
   * The round's problems. Only read for a lead round, where a card is written
   * against the holds counted on its route.
   */
  const [climbs, setClimbs] = useState<RoundScoreClimb[]>([]);
  /**
   * Everybody this viewer may mark in this round, by name, each with their card
   * as the server last sent it back. The pages of the pager.
   */
  const [squad, setSquad] = useState<RoundScore[]>([]);
  /**
   * How each class climbs this lead round, from the board's own meta: put
   * together with a route's forcing, it is what the card's style switch offers.
   */
  const [classStyles, setClassStyles] = useState<LeadClassStyles>({});
  const [picking, setPicking] = useState(false);
  const [loading, setLoading] = useState(true);
  /** The swipe hint, the first time this round is opened here (see swipeHintSeen). */
  const [swipeHint, setSwipeHint] = useState(() => !swipeHintSeen(slug, roundId));

  // Which block's buttons are showing, and which one's marks are being edited.
  const [openCardId, setOpenCardId] = useState<string | null>(null);
  const [editingCardId, setEditingCardId] = useState<string | null>(null);

  // One queue per athlete and block, so three quick taps on the same row land in
  // order and each sees the card the last one left behind.
  const runSerially = useRef(createSerialQueue()).current;
  // What the queued taps reason from: component state may not have caught up.
  const squadRef = useRef<RoundScore[]>([]);

  const rememberCards = useCallback((updated: RoundScore) => {
    squadRef.current = squadRef.current.map((athlete) => (athlete.id === updated.id ? updated : athlete));
    setSquad(squadRef.current);
  }, []);

  const load = useCallback(async () => {
    if (!slug || !roundId) return;
    setLoading(true);
    try {
      const [roundData, page] = await Promise.all([
        competitionRoundsService.getRound(slug, roundId),
        roundScoresService.list(slug, roundId),
      ]);
      setRound(roundData);
      squadRef.current = byAthleteName(page.scores);
      setSquad(squadRef.current);
      setClimbs(page.climbs);
      setClassStyles(
        Object.fromEntries(page.categories.map((category) => [category.id, category.climb_style]))
      );
    } catch (error) {
      toast({
        title: t('errors.loadFailed'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [slug, roundId, t, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Apart from the card's own load, and allowed to fail: without it the navbar
  // keeps DivePeaks' logo, and the card is what the manager came for.
  useEffect(() => {
    if (!slug) return;
    let stale = false;
    competitionsService
      .getCompetitionBySlug(slug)
      .then((data) => {
        if (!stale) setCompetition(data);
      })
      .catch(() => {});
    return () => {
      stale = true;
    };
  }, [slug]);

  /**
   * The round and every card on the board again, at the competition's pace
   * (roundRefreshOptions) and without the skeleton: a judge marks this squad's athletes on the judge-only blocks, the
   * athletes mark their own, and the panel moves the round on. Dropped if one of
   * this page's own saves went out while it was being read (see
   * SerialQueue#quietSince): an answer from before the save would take it back.
   */
  // Bumped with each refresh's board, so a card somebody else marked lights up.
  const [generation, bumpGeneration] = useRefreshGeneration();

  const refresh = useCallback(async () => {
    if (!slug || !roundId) return;
    const mark = runSerially.mark();
    const [roundData, page] = await Promise.all([
      competitionRoundsService.getRound(slug, roundId),
      roundScoresService.list(slug, roundId),
    ]);
    setRound(roundData);
    if (!runSerially.quietSince(mark)) return;
    squadRef.current = byAthleteName(page.scores);
    setSquad(squadRef.current);
    setClimbs(page.climbs);
    bumpGeneration();
  }, [slug, roundId, runSerially, bumpGeneration]);

  useAutoRefresh(refresh, roundRefreshOptions(round, !loading));

  const handleTimeUp = useRoundTimeUp(setRound);
  // Reaching the start only means "go and look": a failed look is retried by
  // the auto-refresh.
  const handleStartReached = useCallback(() => {
    refresh().catch(() => {});
  }, [refresh]);

  // No athlete named: the one last picked in this round, or the first by name.
  // In the address, so a reload keeps them.
  const landing = !participantId
    ? (squad.find((athlete) => athlete.id === loadStoredAthlete(slug, roundId)) ?? squad[0] ?? null)
    : null;
  useEffect(() => {
    if (loading || !landing) return;
    navigate(athletePath(landing.id), { replace: true });
  }, [landing, loading, navigate, athletePath]);

  // The board is already narrowed to this viewer's own athletes by the server,
  // so an id that is not in it is one they may not read.
  const score = squad.find((athlete) => athlete.id === participantId) ?? null;
  const scoreId = score?.id ?? null;

  useEffect(() => {
    if (scoreId) storeAthlete(slug, roundId, scoreId);
    // A new athlete opens with nothing armed.
    setOpenCardId(null);
    setEditingCardId(null);
  }, [scoreId, slug, roundId]);

  const squadIds = useMemo(() => squad.map((athlete) => athlete.id), [squad]);
  const at = participantId ? squadIds.indexOf(participantId) : -1;

  const goTo = useCallback(
    (id: string) => {
      setPicking(false);
      if (id !== participantId) navigate(athletePath(id), { replace: true });
    },
    [navigate, participantId, athletePath]
  );

  // Back to the round as the room reads it: in the team manager's view the
  // round page is this one, and would only send the reader straight back here.
  // A helper's phone goes back to the stage, whose card for this round brings
  // it here again.
  const goBack = () =>
    navigate(helping ? `/competitions/${slug}` : `/competitions/${slug}/rounds/${roundId}?view=public`);

  /**
   * The stage's logo in place of DivePeaks', then the round. The athlete's name
   * is the clock's strip's, under it (see RoundClock), where it has the whole
   * width to itself.
   */
  useBreadcrumb(() => {
    // Only the logo: the stage's name would take the room the round needs.
    const stage = competitionCrumb(competition, () => navigate(`/competitions/${slug}`));
    return {
      onBack: goBack,
      hideHomeLogo: stage.hideHomeLogo,
      segments: round ? [...stage.segments, { label: round.name, onClick: goBack }] : [],
      loading,
    };
  }, [competition, round?.name, loading, slug, roundId]);

  /**
   * Whether the round still takes a squad's marks: its own turn, or the
   * corrections that follow the clock. Once the panel puts it under review the
   * check is the officials' alone, and after it is closed nobody writes here
   * (see Lifecycle::Round).
   */
  const scoringOpen = !!round && (helping ? !!round.can_write : round.running || round.correcting);

  /**
   * A lead round's cards are two numbers rather than a list of goes, which is the
   * only thing that differs on this page — the rows, the judge-only rule, the
   * squad picker and the swipe are the same job either way.
   */
  const isLead = round?.round_type === 'Lead';

  /**
   * The round's state again and nothing else — no skeleton, no board refetch.
   * Asked for when a save is refused because the round has been closed since
   * this page loaded, so `scoringOpen` above can catch up on its own.
   */
  const refreshRound = useCallback(async () => {
    if (!slug || !roundId) return;

    try {
      setRound(await competitionRoundsService.getRound(slug, roundId));
    } catch {
      // The refused save has already said what went wrong.
    }
  }, [slug, roundId]);

  /** The card one athlete has on one problem, as the last answer left it. */
  const cardOf = (athleteId: string, roundClimbId: string) =>
    squadRef.current
      .find((athlete) => athlete.id === athleteId)
      ?.cards.find((card) => card.round_climb_id === roundClimbId);

  const sendCard = (athleteId: string, roundClimbId: string, attempts: AttemptType[]) =>
    runSerially(`${athleteId}:${roundClimbId}`, async () => {
      if (!slug || !roundId) return;
      try {
        rememberCards(
          await roundScoresService.save(slug, roundId, athleteId, [
            { round_climb_id: roundClimbId, attempts },
          ])
        );
      } catch (error) {
        toast({
          title: t('errors.actionFailed', { defaultValue: 'Action failed' }),
          description: error instanceof Error ? error.message : undefined,
          variant: 'destructive',
        });
        // The round may have been put under review or closed from the panel
        // while this page sat open, the refusals a manager can meet without
        // having done anything wrong: ask for the round again so the page puts
        // its own buttons away, instead of waiting to be reloaded to find out.
        if (roundShut(error)) refreshRound();
        // Told them what went wrong; now let the button drop back to idle rather
        // than claim the mark was saved.
        throw error;
      }
    });

  /**
   * One route's card on a lead round: both numbers at once, because that is what
   * a lead card is. Through the same replacement save as a boulder card, and
   * through the same per-problem queue, so two quick saves on one route land in
   * order.
   */
  const sendLeadCard = (athleteId: string, roundClimbId: string, draft: LeadCardDraft) =>
    runSerially(`${athleteId}:${roundClimbId}`, async () => {
      if (!slug || !roundId) return;
      try {
        rememberCards(
          await roundScoresService.save(slug, roundId, athleteId, [
            {
              round_climb_id: roundClimbId,
              holds_count: draft.holds,
              time_seconds: draft.timeSeconds,
              climbed_as: draft.style ?? null,
            },
          ])
        );
        setOpenCardId(null);
      } catch (error) {
        toast({
          title: t('errors.actionFailed', { defaultValue: 'Action failed' }),
          description: error instanceof Error ? error.message : undefined,
          variant: 'destructive',
        });
        if (roundShut(error)) refreshRound();
        throw error;
      }
    });

  /**
   * The card's marks in the shape the scorecard components read. A stored mark
   * always has an id; the index stands in as a key for one the panel's dialog
   * added without saving, which this page never produces but the type allows.
   */
  const drawable = (attempts: ScoreCard['attempts']): ScoredAttempt[] =>
    attempts.map((attempt, index) => ({
      id: attempt.id ?? `unsaved-${index}`,
      attempt_type: attempt.attempt_type,
    }));

  const marksOn = (athleteId: string, roundClimbId: string): AttemptType[] =>
    (cardOf(athleteId, roundClimbId)?.attempts ?? []).map((attempt) => attempt.attempt_type);

  const addAttempt = (athleteId: string, roundClimbId: string, type: AttemptType) =>
    sendCard(athleteId, roundClimbId, [...marksOn(athleteId, roundClimbId), type]);

  /** Takes one mark off, by its place on the card — what the pencil dialog does. */
  const removeAttempt = (athleteId: string, roundClimbId: string, attemptId: string) => {
    const attempts = cardOf(athleteId, roundClimbId)?.attempts ?? [];
    const index = attempts.findIndex((attempt) => attempt.id === attemptId);
    if (index === -1) return;

    const left = attempts.filter((_, i) => i !== index).map((attempt) => attempt.attempt_type);
    if (left.length === 0) setEditingCardId(null);
    return sendCard(athleteId, roundClimbId, left);
  };

  if (loading || landing) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="size-8 animate-spin" />
      </div>
    );
  }

  // Reached by a link before the clock starts: the competition's page is where
  // the squad's athletes in the round are listed until then.
  if (!score && round && !round.running && !round.ended) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 px-4 py-8 text-center" data-testid="round-not-started">
        <p className="text-sm text-muted-foreground">{t('teamManagerAthlete.notStarted')}</p>
        {!helperBase && (
          <Button variant="outline" size="sm" onClick={() => navigate(`/competitions/${slug}`)}>
            {t('teamManagerAthlete.backToCompetition')}
          </Button>
        )}
      </div>
    );
  }

  if (!score) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-8">
        <p className="text-center text-sm text-muted-foreground" data-testid="no-athlete">
          {t(participantId ? 'teamManagerAthlete.notYours' : 'teamManagerAthlete.noAthletes')}
        </p>
      </div>
    );
  }

  /**
   * One athlete's page: who they are, and their card, one problem per group of
   * rows. Only the athlete on screen takes taps — a neighbour drawn while it is
   * swiped in is just read.
   */
  const renderAthlete = (athleteId: string) => {
    const athlete = squad.find((candidate) => candidate.id === athleteId);
    if (!athlete) return null;
    const current = athlete.id === score.id;

    return (
      <div data-testid="athlete-card" data-athlete={athlete.id}>
        <Table>
          <TableBody>
            {athlete.cards.length === 0 && (
              <TableRow>
                <TableCell className="py-6 text-center text-muted-foreground">
                  {t('teamManagerAthlete.noBlocks')}
                </TableCell>
              </TableRow>
            )}

            {athlete.cards.map((card) => {
              const attempts = card.attempts;
              // On a lead round a route is finished when it has a card at all: one
              // go, and the "topped" of a boulder has no counterpart to wait for.
              const topped = isLead ? card.holds_count != null : card.topped;
              // Whether this viewer may write this card here is the server's
              // answer (`can_score`): false on a judge's block for a team manager
              // or a parent, true for the desk, who transcribes anybody's sheet.
              const writable = current && scoringOpen && !!card.can_score;
              // The row tapped last. A lead row opens for a correction too — the
              // editor starts from what is on the card. A topped boulder row is
              // selected but shows only the pencil: there is nothing to add to a top.
              const selected = openCardId === card.round_climb_id && writable;
              const open = selected && (isLead || !topped);
              const select = () => {
                if (writable) setOpenCardId(card.round_climb_id);
              };

              return (
                <Fragment key={card.round_climb_id}>
                  <TableRow
                    className={cn('border-0', topped && 'text-muted-foreground', writable && 'cursor-pointer')}
                    data-state={selected ? 'selected' : undefined}
                    data-testid="block-row"
                    data-writable={writable ? 'true' : 'false'}
                    onClick={select}
                  >
                    <TableCell className="flex items-center gap-2 py-2 font-medium">
                      {card.climb_name}
                      {card.requires_judge && (
                        <Badge
                          variant="outline"
                          className="gap-1"
                          title={t('teamManagerAthlete.judgeOnly')}
                          data-testid="block-judge-only"
                        >
                          <Gavel className="size-3" aria-hidden />
                          {t('teamManagerAthlete.judge')}
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow
                    className={cn(open && 'border-0', writable && 'cursor-pointer')}
                    data-state={selected ? 'selected' : undefined}
                    data-testid="attempts-row"
                    onClick={select}
                  >
                    <TableCell className="pb-2 pt-0">
                      <div className="flex items-center gap-2">
                        <RefreshFlash
                          value={
                            isLead
                              ? leadCardSignature(card)
                              : boulderCardSignature(attempts, !!card.topped, !!card.zone)
                          }
                          className={cn('flex items-center gap-2', topped && 'opacity-60')}
                        >
                          {isLead ? (
                            <LeadCardSummary
                              holds={card.holds_count}
                              timeSeconds={card.time_seconds}
                              formattedTime={card.formatted_time}
                              topped={card.topped}
                              climbedAs={card.climbed_as}
                            />
                          ) : (
                            <BoulderAttemptMarkers attempts={drawable(attempts)} />
                          )}
                        </RefreshFlash>

                        {/* Only on the selected row, as on the judge's page. */}
                        {selected && !isLead && attempts.length > 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            data-testid="edit-attempts-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingCardId(card.round_climb_id);
                            }}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>

                  {open && !isLead && (
                    <TableRow data-state="selected" data-testid="actions-row">
                      <TableCell className="pb-3 pt-0">
                        <BoulderActionButtons
                          topped={topped}
                          onRecordAttempt={() => addAttempt(athlete.id, card.round_climb_id, 'attempt')}
                          onRecordZone={() => addAttempt(athlete.id, card.round_climb_id, 'zone')}
                          onRecordTop={() => addAttempt(athlete.id, card.round_climb_id, 'top')}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  /** The route the lead dialog is open on, and whatever is on its card. */
  const openLeadCard = isLead
    ? (score.cards.find((card) => card.round_climb_id === openCardId) ?? null)
    : null;
  const openLeadRoute = openLeadCard
    ? (climbs.find((climb) => climb.id === openLeadCard.round_climb_id) ?? null)
    : null;
  const editing = editingCardId ? score.cards.find((card) => card.round_climb_id === editingCardId) : null;

  return (
    // No padding of its own on top: the page's container already has some, and
    // the card is what the manager came for.
    <RefreshGenerationContext.Provider value={generation}>
      <div className="flex w-full flex-col pb-4">
        {/* The round's state and clock in the strip under the navbar, as on
            the round page — a manager walking the card needs to know how long
            is left — opened by the athlete's name, which is the way into the
            squad: a manager marking four athletes moves between them far more
            often than they go back. */}
        {round && (
          <RoundClock
            round={round}
            onTimeUp={handleTimeUp}
            onStartReached={handleStartReached}
            subject={
              <RoundClockSubject
                label={score.full_name || ''}
                onPick={squad.length > 1 ? () => setPicking(true) : undefined}
              />
            }
          />
        )}

        <RoundHelperStrip canWrite={scoringOpen} className="mt-3" />

        {/* One page per athlete, swiped through in the order of their names. */}
        {squad.length > 1 && at >= 0 ? (
          <SwipePager
            ids={squadIds}
            activeId={score.id}
            onChange={goTo}
            renderPage={renderAthlete}
            peek={swipeHint}
            onPeek={() => {
              storeSwipeHintSeen(slug, roundId);
              setSwipeHint(false);
            }}
            className="-mx-4 sm:-mx-6"
            pageClassName="px-4 sm:px-6"
          />
        ) : (
          renderAthlete(score.id)
        )}

        {!scoringOpen && !helping && (
          <p className="pt-4 text-center text-xs text-muted-foreground" data-testid="squad-scoring-shut">
            {round?.under_review ? t('competitionRounds.underReviewNotice') : t('teamManagerAthlete.closed')}
          </p>
        )}

        {/* On a lead round a row opens the whole climb instead of three buttons:
            the clock, the route's drawing, the two numbers. Saving closes it, and
            the table behind is where the next route is chosen. */}
        {isLead && (
          <LeadScoringDialog
            open={openLeadCard !== null}
            onOpenChange={(next) => !next && setOpenCardId(null)}
            athleteName={score.full_name ?? ''}
            routeName={openLeadCard?.climb_name ?? ''}
            imageUrl={openLeadRoute?.image_url}
            holds={openLeadRoute?.holds}
            maxHolds={openLeadRoute?.holds_count}
            cardHolds={openLeadCard?.holds_count ?? null}
            cardTimeSeconds={openLeadCard?.time_seconds ?? null}
            cardFormattedTime={openLeadCard?.formatted_time}
            cardClimbedAs={openLeadCard?.climbed_as}
            styles={permittedLeadStyles(openLeadRoute, score.category_id, classStyles)}
            readOnly={!scoringOpen || !openLeadCard?.can_score}
            onSave={(draft) => {
              if (openLeadCard) return sendLeadCard(score.id, openLeadCard.round_climb_id, draft);
            }}
            onClear={
              openLeadCard?.holds_count != null
                ? () => {
                    if (openLeadCard) {
                      return sendLeadCard(score.id, openLeadCard.round_climb_id, {
                        holds: '',
                        timeSeconds: null,
                        style: null,
                      });
                    }
                  }
                : undefined
            }
          />
        )}

        {/* The squad as a list to pick from, from the bottom of the screen: this
            round, this viewer's athletes, by name. */}
        <Sheet open={picking} onOpenChange={setPicking}>
          <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{t('teamManagerAthlete.pickTitle')}</SheetTitle>
            </SheetHeader>

            <div className="mt-4 space-y-1">
              {squad.map((athlete) => {
                const current = athlete.id === participantId;
                // The same count the table shows: what is left to mark.
                const pending = athlete.cards.filter((card) =>
                  isLead ? card.holds_count == null : card.attempts.length === 0
                ).length;

                return (
                  <button
                    key={athlete.id}
                    type="button"
                    onClick={() => goTo(athlete.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left',
                      current ? 'bg-muted' : 'hover:bg-muted/50'
                    )}
                    data-testid="squad-option"
                    aria-current={current || undefined}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{athlete.full_name}</span>
                      <span className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                        {athlete.category_name && <span>{athlete.category_name}</span>}
                        {athlete.team_name && <span>· {athlete.team_name}</span>}
                      </span>
                    </span>
                    <Badge variant="outline" className="shrink-0 text-xs">
                      {pending === 0
                        ? t('teamManagerAthletes.allMarked')
                        : t('teamManagerAthletes.pending', {
                            count: pending,
                            total: athlete.cards.length,
                          })}
                    </Badge>
                    {current && <Check className="size-4 shrink-0" aria-hidden />}
                  </button>
                );
              })}
            </div>
          </SheetContent>
        </Sheet>

        <BoulderEditDialog
          open={!!editingCardId}
          onOpenChange={(next) => !next && setEditingCardId(null)}
          attempts={
            // Only the stored marks can be taken off one at a time; an unsaved one
            // has no id to name.
            (editing?.attempts ?? []).flatMap((attempt) =>
              attempt.id ? [{ id: attempt.id, attempt_type: attempt.attempt_type }] : []
            )
          }
          onRemoveAttempt={(attemptId) => {
            if (editingCardId) removeAttempt(score.id, editingCardId, attemptId);
          }}
        />
      </div>
    </RefreshGenerationContext.Provider>
  );
}
